import React, { useMemo, useCallback } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
	DndContext,
	DragEndEvent,
	MouseSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Checkbox } from '@/common/ui/checkbox'
import { Badge } from '@/common/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { ChevronDown, GripVertical, X } from 'lucide-react'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'

interface GamePickerProps {
	selectedIds: string[]
	onIdsChange: (ids: string[]) => void
	disabled?: boolean
	maxItems?: number
}

interface SortableGameBadgeProps {
	gameId: string
	displayName: string
	index: number
	onRemove: (id: string) => void
}

function SortableGameBadge({
	gameId,
	displayName,
	index,
	onRemove,
}: SortableGameBadgeProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: gameId })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	}

	return (
		<div ref={setNodeRef} style={style} className="flex items-center gap-1">
			<Badge
				variant="secondary"
				className="gap-1 rounded-sm py-0.5 pl-1 pr-2 text-xs font-normal"
			>
				<span
					className="flex cursor-grab items-center text-muted-foreground hover:text-foreground"
					{...attributes}
					{...listeners}
				>
					<GripVertical className="h-3 w-3" />
				</span>
				<span className="text-muted-foreground">{index + 1}.</span>
				{displayName}
				<X
					className="h-3 w-3 cursor-pointer hover:opacity-70"
					onClick={() => onRemove(gameId)}
				/>
			</Badge>
		</div>
	)
}

export const GamePicker = ({
	selectedIds,
	onIdsChange,
	disabled = false,
	maxItems = 10,
}: GamePickerProps) => {
	const [search, setSearch] = React.useState('')
	const [isOpen, setIsOpen] = React.useState(false)
	const [scrollElement, setScrollElement] =
		React.useState<HTMLDivElement | null>(null)

	const sensors = useSensors(
		useSensor(MouseSensor, {
			activationConstraint: { distance: 5 },
		}),
		useSensor(TouchSensor, {
			activationConstraint: { delay: 150, tolerance: 5 },
		}),
	)

	const { data: gamesData } = useQueryGetGameList({
		skip: 0,
		take: 10000,
		includeHardProviders: false,
	})

	const gameItems = useMemo(() => {
		return (gamesData?.data ?? []).map((game) => {
			const gameName = game.displayName || game.name
			const providerLabel = game.providerName
			return {
				id: game.id,
				code: game.code,
				name: game.name,
				displayName: providerLabel
					? `${gameName} (${providerLabel})`
					: gameName,
			}
		})
	}, [gamesData])

	const filteredItems = useMemo(() => {
		if (!search) return gameItems
		const lowerSearch = search.toLowerCase()
		return gameItems.filter(
			(item) =>
				item.name.toLowerCase().includes(lowerSearch) ||
				item.displayName.toLowerCase().includes(lowerSearch) ||
				item.code.toLowerCase().includes(lowerSearch),
		)
	}, [gameItems, search])

	const rowVirtualizer = useVirtualizer({
		count: filteredItems.length,
		getScrollElement: () => scrollElement,
		estimateSize: () => 36,
		overscan: 5,
	})

	React.useEffect(() => {
		if (scrollElement) {
			scrollElement.scrollTop = 0
		}
	}, [search, scrollElement])

	const isAtLimit = selectedIds.length >= maxItems

	const handleItemToggle = useCallback(
		(id: string) => {
			const isSelected = selectedIds.includes(id)
			if (isSelected) {
				onIdsChange(selectedIds.filter((i) => i !== id))
			} else if (!isAtLimit) {
				onIdsChange([...selectedIds, id])
			}
		},
		[selectedIds, onIdsChange, isAtLimit],
	)

	const handleRemoveItem = useCallback(
		(id: string) => {
			onIdsChange(selectedIds.filter((i) => i !== id))
		},
		[selectedIds, onIdsChange],
	)

	const handleClear = useCallback(() => {
		onIdsChange([])
	}, [onIdsChange])

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			if (!over || active.id === over.id) return

			const oldIndex = selectedIds.indexOf(active.id as string)
			const newIndex = selectedIds.indexOf(over.id as string)
			onIdsChange(arrayMove(selectedIds, oldIndex, newIndex))
		},
		[selectedIds, onIdsChange],
	)

	const getItemDisplayName = useCallback(
		(id: string) => {
			const item = gameItems.find((i) => i.id === id)
			return item?.displayName || id
		},
		[gameItems],
	)

	const selectedCount = selectedIds.length

	return (
		<div className="space-y-3">
			<Popover
				open={isOpen}
				onOpenChange={(open) => {
					setIsOpen(open)
					if (!open) setSearch('')
				}}
			>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						className="w-full justify-between"
						disabled={disabled}
						type="button"
					>
						<span className="truncate">
							{selectedCount > 0
								? `${selectedCount}/${maxItems} เกมที่เลือก`
								: 'เลือกเกม'}
						</span>
						<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-full p-0" align="start">
					<div className="space-y-2 p-2">
						<Input
							placeholder="ค้นหาเกม..."
							className="h-8 text-sm"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						{filteredItems.length > 0 ? (
							<div ref={setScrollElement} className="h-[200px] overflow-y-auto">
								<div
									style={{
										height: `${rowVirtualizer.getTotalSize()}px`,
										width: '100%',
										position: 'relative',
									}}
								>
									{rowVirtualizer.getVirtualItems().map((virtualRow) => {
										const item = filteredItems[virtualRow.index]
										if (!item) return null

										const isChecked = selectedIds.includes(item.id)
										const isDisabled = !isChecked && isAtLimit

										return (
											<div
												key={item.id}
												style={{
													position: 'absolute',
													top: 0,
													left: 0,
													width: '100%',
													height: `${virtualRow.size}px`,
													transform: `translateY(${virtualRow.start}px)`,
												}}
											>
												<div
													className={`flex items-center space-x-2 rounded-sm px-2 py-1.5 ${
														isDisabled
															? 'cursor-not-allowed opacity-50'
															: 'cursor-pointer hover:bg-accent'
													}`}
													onClick={() =>
														!isDisabled && handleItemToggle(item.id)
													}
												>
													<Checkbox
														checked={isChecked}
														disabled={isDisabled}
														className="pointer-events-none h-4 w-4"
													/>
													<span className="text-xs">{item.displayName}</span>
												</div>
											</div>
										)
									})}
								</div>
							</div>
						) : (
							<div className="flex h-[200px] items-center justify-center">
								<div className="text-center text-xs text-muted-foreground">
									ไม่พบเกม
								</div>
							</div>
						)}
						{selectedCount > 0 && (
							<Button
								variant="ghost"
								className="w-full justify-center text-xs"
								onClick={handleClear}
								type="button"
							>
								ล้างทั้งหมด
							</Button>
						)}
					</div>
				</PopoverContent>
			</Popover>

			{selectedCount > 0 && (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						items={selectedIds}
						strategy={verticalListSortingStrategy}
					>
						<div className="flex flex-wrap gap-1.5">
							{selectedIds.map((id, index) => (
								<SortableGameBadge
									key={id}
									gameId={id}
									displayName={getItemDisplayName(id)}
									index={index}
									onRemove={handleRemoveItem}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>
			)}
		</div>
	)
}
